import React, { useCallback, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";

import { useMount } from "@sportal/cdk/hooks";
import { uuid } from "@sportal/lib";
import { getImage, IMAGE_VALIDATION_ERRORS } from "./imageUpload.helpers";
import { CustomValidators } from "./upload.types";
import { useIntlTranslate } from "../../../../hooks";
import { ImageUploadStatus } from "./ImageUploadStatus";
import { ImageUploadLegend } from "./ImageUploadLegend";
import { ImageUploadField } from "./ImageUploadField";
import { ImageUploadControls } from "./ImageUploadControls";

import "./ImageUpload.scss";

const LOGO_KEY = "sb-branding-logo";

const setLogoInStorage = (logo: string): void => {
  localStorage.setItem(LOGO_KEY, logo);
};
export const clearLogoFromStorage = (): void => {
  localStorage.removeItem(LOGO_KEY);
};

interface ImageUploadProps {
  imageUrl: string;
  acceptedFileTypes: string;
  onUploadSuccess: (image?: string) => void;
  customValidators: CustomValidators;
  imageAltText: string;
}

export const ImageUpload = ({
  imageUrl,
  acceptedFileTypes,
  customValidators,
  onUploadSuccess,
  imageAltText,
}: ImageUploadProps) => {
  const translate = useIntlTranslate();
  const [validationError, setValidationError] = useState(null);
  const [successStatus, setSuccessStatus] = useState(null);

  useMount(() => {
    clearLogoFromStorage();
  });

  const handleFileUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawImage = e.target.files[0];
      if (!rawImage) return;

      let image: string;
      try {
        image = await getImage(rawImage);
      } catch (e) {
        setValidationError(IMAGE_VALIDATION_ERRORS.IMAGE_TYPE.message);

        return;
      }

      const reader = new FileReader();

      const validationError = await customValidators(rawImage);

      if (validationError) {
        setValidationError(validationError.shortMessage);

        return;
      }

      setValidationError(null);
      setSuccessStatus(<FormattedMessage id="aria_success_image_upload" />);

      reader.readAsDataURL(rawImage);
      reader.onloadend = (e) => {
        setLogoInStorage(e.target.result as string);
      };

      onUploadSuccess(image);
    },
    [customValidators, onUploadSuccess]
  );

  const handleFileRemove = useCallback(() => {
    setValidationError(null);
    setSuccessStatus(<FormattedMessage id="aria_image_removed" />);
    clearLogoFromStorage();
    onUploadSuccess();
  }, [onUploadSuccess]);

  const inputRef = useRef<HTMLInputElement>(null);
  const fireUpload = () => inputRef.current.click();

  return (
    <div className="image-upload">
      <input
        ref={inputRef}
        hidden
        id="image-upload"
        key={uuid()}
        type="file"
        onChange={handleFileUpload}
        accept={acceptedFileTypes}
        data-testid="image-upload-input"
      />
      <div className="image-upload__logo">
        {imageUrl ? (
          <div className="image-upload__preview" data-testid="image-preview">
            <img src={imageUrl} alt={translate(imageAltText)} />
          </div>
        ) : (
          <span className="sr-only">
            <FormattedMessage id="aria_no_image" />
          </span>
        )}
        {!imageUrl && <ImageUploadField openUpload={fireUpload} />}
      </div>
      <ImageUploadStatus
        status={successStatus}
        validationError={validationError}
      />
      <ImageUploadLegend />
      {imageUrl && (
        <ImageUploadControls
          openUpload={fireUpload}
          onRemoveFile={handleFileRemove}
        />
      )}
    </div>
  );
};
